import React from "react";
import { useFieldArray, Controller } from "react-hook-form";
import { compact } from 'lodash';

import { FormStepProps } from "../../../screens/ReviewFormScreen";

import UserSearch from "../../utils/search/UserSearch";
import useTranslations from "../../../hooks/useTranslations";
import { Button, Checkbox } from "../../wombat-ui";
import { CloseIcon } from "../../icons";
import UserAvatar from "../../utils/avatars/UserAvatar";
import { ReviewFullFragment, UserMinimalFragment } from '../../../graphql/generated/definitions';

export const SelectedSupervisor: React.FC<
  Pick<FormStepProps, "register" | "control"> & {
    user: UserMinimalFragment;
    index: number;
    isLast: boolean;
    baseId: string;
    onDelete: () => void;
    name: string;
    defaultValue: boolean;
  }
> = ({
  user,
  baseId,
  isLast,
  index,
  register,
  control,
  onDelete,
  name,
  defaultValue
}) => {
  const t = useTranslations();

  return (
    <div
      className={`flex items-center justify-between w-full max-w-2xl ${
        index === 0 && "pt-2"
      } pb-2 ${!isLast && "border-b"} border-light-500`}
    >
      <input
        type="hidden"
        value={user.id}
        ref={register()}
        id={`${name}.userId`}
        name={`${name}.userId`}
      />
      <div className="flex items-center">
        <UserAvatar src={user.avatar} alt="" size={6} />
        <p className="ml-4 text-sm font-medium text-dark-100">{user.name}</p>
      </div>
      <div className="flex items-center">
        <Controller
          control={control}
          name={`${name}.emailNotificationsEnabled`}
          id={`${name}.emailNotificationsEnabled`}
          defaultValue={defaultValue}
          render={(
            { onChange, value, name, ...props },
          ) => {
            return (
              <Checkbox
                variant="boxed"
                label={t("reviews.receiveEmailNotification") as string}
                onChange={(e) => onChange(e.target.checked)}
                checked={value}
                name={name}
                id={name}
                {...props}
              />
            );
          }}
        />
        <Button
          scale="xs"
          variant="ghost"
          icon={CloseIcon}
          id={`${baseId}${user.id}-user-remove`}
          onClick={onDelete}
        >
          <span className="sr-only">
            {t("forms.actions.removeEntity", { entity: user.name })}
          </span>
        </Button>
      </div>
    </div>
  );
};

const ReviewSupervisorsForm: React.FC<
  Omit<FormStepProps, "onNext"> & { defaultValues?: ReviewFullFragment; name: string; }
> = ({
  register,
  control,
  defaultValues,
  name
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name,
    keyName: "key",
  });

  return (
    <UserSearch
      defaultValue={
        fields.length > 0
          ? (fields.map(({ userId }) => userId) as string[])
          : defaultValues?.reviewSupervisors.map(({ user }) => user.id)
      }
      baseId={name}
      onSelect={({ id }) => {
        append({ userId: id, emailNotificationsEnabled: true });
      }}
      onRemove={({ id }) => {
        remove(fields.findIndex(({ userId }) => id === userId));
      }}
    >
      {({ users, handleUserDelete }) => {
        // Synchronize field and user list
        const displayedUsers = fields.map((field) => {
          const user = users.find(({ id }) => id === field.userId);

          if (!user) {
            return (null);
          }

          return ({
            field, user
          })
        });

        return (
          compact(displayedUsers).map(({ user, field }, index) => (
            <SelectedSupervisor
              defaultValue={field.emailNotificationsEnabled}
              key={field.key}
              user={user}
              baseId={name}
              index={index}
              isLast={index === fields.length - 1}
              register={register}
              control={control}
              name={`${name}[${index}]`}
              onDelete={() => {
                handleUserDelete(user.id);
              }}
            />
          ))
        );

        return (null);
      }}
    </UserSearch>
  );
};

export default React.memo(ReviewSupervisorsForm);
